import React, { useMemo, useState } from 'react';
import { components } from 'react-select';
import Select from '../../components/Select';
import LocalSearchHighlight from '../../search/LocalSearchHighlight';
import type { FuzzySearchResult } from '../../utils/search/fuzzySearch';
import fuzzySearch, { FuzzySearchIndex } from '../../utils/search/fuzzySearch';
import styles from './SearchableSelectByName.module.scss';

const MAX_RESULTS_DISPLAYED = 50;

type Item = {
  id: string;
  name: string;
};

interface SearchableSelectByNameProps {
  items: Item[];
  renderItem: (
    item: { id: string; name: React.ReactNode },
    value: string,
  ) => React.ReactNode;
  placeholder: string;
  onSelect: (item: Item) => void;
  onClose: () => void;
}

const SearchableSelectByName = ({
  items,
  renderItem,
  placeholder,
  onSelect,
  onClose,
}: SearchableSelectByNameProps) => {
  const [value, setValue] = useState('');

  const index = useMemo(() => new FuzzySearchIndex(items, ['name']), [items]);
  const options: (FuzzySearchResult<Item> | { item: Item })[] = (
    value ? fuzzySearch(value, index) : items.map((item) => ({ item }))
  ).slice(0, MAX_RESULTS_DISPLAYED);

  return (
    <Select
      className={styles.select}
      autoFocus
      blurInputOnSelect={false}
      closeMenuOnSelect={false}
      components={{
        DropdownIndicator: null,
        // eslint-disable-next-line react/no-unstable-nested-components -- Fix next time editing the file
        Option: (option) => (
          <components.Option {...option}>
            {renderItem(
              {
                ...option.data.item,
                name:
                  'matches' in option.data && option.data.matches ? (
                    <LocalSearchHighlight
                      matches={option.data.matches}
                      matchKey="name"
                      defaultValue={option.data.item.name}
                    />
                  ) : (
                    option.data.item.name
                  ),
              },
              value,
            )}
          </components.Option>
        ),
      }}
      filterOption={null}
      getOptionLabel={({ item: { name } }) => name}
      getOptionValue={({ item: { id } }) => id}
      inputValue={value}
      onInputChange={setValue}
      simpleValue={false}
      options={options}
      onChange={(val) => val?.item && onSelect(val.item)}
      placeholder={placeholder}
      onMenuClose={onClose}
      menuIsOpen
    />
  );
};

export default SearchableSelectByName;
